// Shared transaction-table display helpers: category color, P/B indicator,
// colored category pill, link icon, multi-key sorting, and a full TransactionsTable.
import { useEffect, useRef } from 'react'
import { usePersistedState } from '../hooks/usePersisted'
import type { CSSProperties, ReactNode } from 'react'
import type { Transaction } from '../types'

const PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#f59e0b', '#10b981', '#06b6d4', '#3b82f6', '#84cc16']
const PAGE_SIZE = 100
const money = (v: number) => '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Deterministic color for a category name (consistent across all pages). */
export function catColor(name: string | null | undefined): string {
  if (!name) return '#94a3b8'
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return PALETTE[h % PALETTE.length]
}

/** Personal/Business indicator for a transaction line (null businessId = Personal). */
export function PBBadge({ businessId }: { businessId?: number | null }) {
  const isBiz = businessId != null
  return (
    <span title={isBiz ? 'Business' : 'Personal'} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 15, height: 15, padding: '0 3px', borderRadius: 4, flexShrink: 0,
      fontSize: '0.58rem', fontWeight: 800, lineHeight: 1,
      background: isBiz ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.14)',
      color: isBiz ? 'var(--pb-primary)' : '#059669',
    }}>{isBiz ? 'B' : 'P'}</span>
  )
}

/** Category/subcategory rendered as a tinted pill in the category's own color (like tags). */
export function CatPill({ category, subCategory }: { category: string | null | undefined; subCategory?: string | null }) {
  if (!category) return <span style={{ color: 'var(--pb-muted)' }}>—</span>
  const c = catColor(category)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
      fontSize: '0.78rem', fontWeight: 600, padding: '0.12rem 0.5rem', borderRadius: 6,
      background: c + '22', color: c, border: `1px solid ${c}55`,
    }}>
      {category}
      {subCategory && <span style={{ opacity: 0.8, fontWeight: 500, fontSize: '0.72rem' }}>· {subCategory}</span>}
    </span>
  )
}

/** Invoice/Receipt link status icon (matches the Business transactions table). */
export function LinkIcon({ status }: { status?: 'Linked' | 'NeedsReview' | 'Invoice' | 'Unlinked' | null }) {
  if (status === 'Invoice')
    return <i className="bi bi-file-earmark-text-fill" title="Linked to invoice" style={{ color: '#6366f1', fontSize: '0.9rem' }} />
  if (status === 'Linked')
    return <i className="bi bi-receipt" title="Linked to receipt" style={{ color: '#16a34a', fontSize: '0.9rem' }} />
  if (status === 'NeedsReview')
    return <i className="bi bi-exclamation-triangle-fill" title="Receipt needs review" style={{ color: '#d97706', fontSize: '0.9rem' }} />
  return <i className="bi bi-dash-circle" title="Unlinked" style={{ color: 'var(--pb-muted)', fontSize: '0.9rem', opacity: 0.5 }} />
}

// ── Progressive multi-key sorting (generic, reusable across any table) ───────────
export type SortDir = 'asc' | 'desc'
export type SortKey<F extends string = string> = { field: F; dir: SortDir }

/** Promote `field` to the front of the sort hierarchy (or toggle direction if already primary). */
export function promoteSort<F extends string>(prev: SortKey<F>[], field: F, descFirst: F[] = []): SortKey<F>[] {
  if (prev[0]?.field === field) return [{ field, dir: prev[0].dir === 'asc' ? 'desc' : 'asc' }, ...prev.slice(1)]
  return [{ field, dir: descFirst.includes(field) ? 'desc' : 'asc' }, ...prev.filter(k => k.field !== field)]
}

/** Stable multi-key sort using a value accessor. */
export function multiSortBy<T, F extends string>(items: T[], keys: SortKey<F>[], get: (t: T, f: F) => string | number | null | undefined): T[] {
  if (keys.length === 0) return items
  return [...items].sort((a, b) => {
    for (const k of keys) {
      const av = get(a, k.field), bv = get(b, k.field)
      const an = av ?? '', bn = bv ?? ''
      const cmp = typeof an === 'number' && typeof bn === 'number' ? an - bn : String(an).localeCompare(String(bn))
      if (cmp !== 0) return k.dir === 'asc' ? cmp : -cmp
    }
    return 0
  })
}

/** Sortable table header cell — shows a direction chevron when its field is part of the sort. */
export function SortableTh({ label, field, sortKeys, onSort, right, style }: {
  label: string; field: string; sortKeys: SortKey[]; onSort: () => void; right?: boolean; style?: CSSProperties
}) {
  const idx = sortKeys.findIndex(k => k.field === field)
  const active = idx >= 0
  return (
    <th onClick={onSort} style={{
      cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
      color: idx === 0 ? 'var(--pb-primary)' : active ? 'var(--pb-text)' : 'var(--pb-muted)',
      padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)', transition: 'color 0.15s',
      textAlign: right ? 'right' : 'left', ...style,
    }}>
      {label}
      {active && <i className={`bi ms-1 ${sortKeys[idx].dir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: '0.65rem' }} />}
    </th>
  )
}

// ── Shared transactions table ───────────────────────────────────────────────────
type TxField = 'date' | 'account' | 'merchant' | 'category' | 'tags' | 'charges'
const TX_DEFAULT_SORT: SortKey<TxField>[] = [
  { field: 'date', dir: 'desc' }, { field: 'merchant', dir: 'asc' }, { field: 'charges', dir: 'asc' },
]
function txValue(t: Transaction, f: TxField): string | number {
  switch (f) {
    case 'date':     return t.date ?? ''
    case 'account':  return (t.account ?? '').toLowerCase()
    case 'merchant': return (t.merchantDisplayName ?? '').toLowerCase()
    case 'category': return (t.category ?? '').toLowerCase()
    case 'tags':     return (t.tag ?? '').toLowerCase()
    case 'charges':  return t.charges ?? 0
  }
}

function TxRow({ t, onClick, showLink }: { t: Transaction; onClick: () => void; showLink: boolean }) {
  const isIncome = (t.charges ?? 0) < 0   // negative = money in → green "+"
  const tags = t.tag ? t.tag.split(',').map(s => s.trim()).filter(Boolean) : []
  const isSplit = !!(t.isSplit && t.splitLines && t.splitLines.length > 0)
  const TD: CSSProperties = { borderBottom: '1px solid var(--pb-border)', verticalAlign: 'top' }
  return (
    <tr onClick={onClick} className="pb-table-row" style={{ cursor: 'pointer' }}>
      <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--pb-muted)', fontSize: '0.78rem', padding: '0.55rem 0.6rem' }}>{t.date ?? '—'}</td>
      <td style={{ ...TD, color: 'var(--pb-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap', padding: '0.55rem 0.6rem' }}>{t.account ?? '—'}</td>
      <td style={{ ...TD, maxWidth: 200, padding: '0.55rem 0.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, flex: 1 }}>{t.merchantDisplayName ?? <span style={{ color: 'var(--pb-muted)' }}>—</span>}</span>
          {t.isPending && <span style={{ flexShrink: 0, fontSize: '0.62rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: 4, background: 'rgba(234,179,8,0.12)', color: '#b45309', border: '1px solid rgba(234,179,8,0.3)' }}>pending</span>}
        </div>
      </td>
      {isSplit ? (
        <td colSpan={2} style={{ ...TD, padding: '0.55rem 0.6rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {t.splitLines!.map((line, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(148,163,184,0.08)', border: '1px solid var(--pb-border)', borderRadius: 6, padding: '0.28rem 0.6rem', minHeight: '1.65rem' }}>
                <PBBadge businessId={line.businessId} />
                <CatPill category={line.category} subCategory={line.subCategory} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem', flex: 1, justifyContent: 'flex-end' }}>
                  {line.tags && line.tags.length > 0
                    ? line.tags.map(tg => <span key={tg} style={{ display: 'inline-block', fontSize: '0.64rem', fontWeight: 600, padding: '0.05rem 0.3rem', borderRadius: 4, background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)', whiteSpace: 'nowrap' }}>{tg}</span>)
                    : null}
                </div>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, minWidth: 70, textAlign: 'right' }}>{money(line.amount)}</span>
              </div>
            ))}
          </div>
        </td>
      ) : (
        <>
          <td style={{ ...TD, padding: '0.55rem 0.6rem' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <PBBadge businessId={t.primaryLineBusinessId} />
              <CatPill category={t.category} subCategory={t.subCategory} />
            </span>
          </td>
          <td style={{ ...TD, fontSize: '0.82rem', padding: '0.55rem 0.6rem' }}>
            {tags.length > 0
              ? tags.map(tg => <span key={tg} style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.45rem', borderRadius: 6, marginRight: '0.2rem', background: 'rgba(99,102,241,0.1)', color: 'var(--pb-primary)' }}>{tg}</span>)
              : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
          </td>
        </>
      )}
      <td style={{ ...TD, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: isIncome ? '#16a34a' : 'inherit', padding: '0.55rem 0.6rem' }}>
        {t.charges != null ? ((isIncome ? '+' : '') + money(t.charges)) : '—'}
      </td>
      {showLink && <td style={{ ...TD, textAlign: 'center', padding: '0.55rem 0.6rem' }}><LinkIcon status={t.linkStatus} /></td>}
      <td style={{ ...TD, textAlign: 'right', width: 36, paddingLeft: 0, padding: '0.55rem 0.6rem' }}><i className="bi bi-chevron-right pb-row-chev" /></td>
    </tr>
  )
}

/**
 * Self-contained transactions table: count header, optional Personal/Business filter,
 * progressive multi-key sorting on every column, split rows aligned in one card,
 * green "+" income amounts, optional Link column, and client-side pagination.
 * Pass the already-filtered transaction list; `resetKey` resets to page 1 when it changes.
 */
export function TransactionsTable({ items, onRowClick, showPB = false, showLink = false, resetKey = '', headerExtra, stateKey = 'default' }: {
  items: Transaction[]
  onRowClick: (id: string) => void
  showPB?: boolean
  showLink?: boolean
  resetKey?: string
  headerExtra?: ReactNode
  /** Namespace for persisting sort/filter/page across navigation (e.g. opening an edit). */
  stateKey?: string
}) {
  const [sortKeys, setSortKeys] = usePersistedState<SortKey<TxField>[]>(`txt:${stateKey}:sort`, TX_DEFAULT_SORT)
  const [pbFilter, setPbFilter] = usePersistedState<'all' | 'P' | 'B'>(`txt:${stateKey}:pb`, 'all')
  const [page, setPage] = usePersistedState<number>(`txt:${stateKey}:page`, 1)

  // Reset to page 1 when the filter context or sort changes — but NOT on the initial
  // mount, so a page restored from sessionStorage (returning from edit) is preserved.
  const skipReset = useRef(true)
  useEffect(() => {
    if (skipReset.current) { skipReset.current = false; return }
    setPage(1)
  }, [resetKey, pbFilter, sortKeys]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (f: TxField) => setSortKeys(prev => promoteSort(prev, f, ['date']))

  const pbMatch = (t: Transaction): boolean => {
    if (pbFilter === 'all') return true
    const ids = (t.isSplit && t.splitLines?.length) ? t.splitLines.map(l => l.businessId ?? null) : [t.primaryLineBusinessId ?? null]
    return pbFilter === 'P' ? ids.some(b => b == null) : ids.some(b => b != null)
  }

  const sorted = multiSortBy(items.filter(pbMatch), sortKeys, txValue)
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const curPage = Math.min(page, totalPages)
  const pageItems = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE)
  const baseCols = showLink ? 8 : 7

  return (
    <div className="pb-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="pb-kpi-label">
          <i className="bi bi-list-ul me-1" />Transactions
          <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--pb-muted)', fontSize: '0.82rem' }}>({sorted.length.toLocaleString()})</span>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {headerExtra}
          {showPB && (
            <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
              {(['all', 'P', 'B'] as const).map(v => (
                <button key={v} className={`pb-chart-sel-btn${pbFilter === v ? ' active' : ''}`} onClick={() => setPbFilter(v)}>
                  {v === 'all' ? 'All' : v}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <SortableTh label="Date"     field="date"     sortKeys={sortKeys} onSort={() => onSort('date')} />
              <SortableTh label="Account"  field="account"  sortKeys={sortKeys} onSort={() => onSort('account')} />
              <SortableTh label="Merchant" field="merchant" sortKeys={sortKeys} onSort={() => onSort('merchant')} />
              <SortableTh label="Category" field="category" sortKeys={sortKeys} onSort={() => onSort('category')} />
              <SortableTh label="Tags"     field="tags"     sortKeys={sortKeys} onSort={() => onSort('tags')} />
              <SortableTh label="Amount"   field="charges"  sortKeys={sortKeys} onSort={() => onSort('charges')} right />
              {showLink && <th style={{ padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)', textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--pb-muted)' }}>Link</th>}
              <th style={{ padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)', width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr><td colSpan={baseCols} style={{ padding: '2.5rem 0.6rem', borderBottom: '1px solid var(--pb-border)', textAlign: 'center', color: 'var(--pb-muted)' }}><i className="bi bi-inbox me-2" />No transactions found.</td></tr>
            )}
            {pageItems.map(t => <TxRow key={t.id} t={t} onClick={() => onRowClick(t.id)} showLink={showLink} />)}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="pb-pager">
          <div className="pb-pager-info">
            Showing <strong>{(curPage - 1) * PAGE_SIZE + 1}&ndash;{Math.min(curPage * PAGE_SIZE, sorted.length)}</strong> of <strong>{sorted.length.toLocaleString()}</strong>
          </div>
          <div className="pb-pager-controls">
            <button className="pb-pager-btn" disabled={curPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><i className="bi bi-chevron-left" /> Prev</button>
            <span className="pb-pager-current">Page {curPage} of {totalPages}</span>
            <button className="pb-pager-btn" disabled={curPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next <i className="bi bi-chevron-right" /></button>
          </div>
        </div>
      )}
    </div>
  )
}
